import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {NgClass, NgForOf, NgIf, UpperCasePipe} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {WizardDataService} from "../../../services/wizard-data.service";

interface LanguageStatus {
    code: string;
    status: 'pending' | 'processing' | 'complete' | 'skipped' | 'error';
    message: string;
}

@Component({
    selector: 'app-step-three-process',
    standalone: true,
    imports: [
        NgClass,
        MatIcon,
        MatProgressSpinner,
        UpperCasePipe,
        NgForOf,
        NgIf
    ],
    templateUrl: './step-three-process.component.html',
    styleUrl: './step-three-process.component.scss'
})
export class StepThreeProcessComponent implements OnInit, OnDestroy {
    @Output() completed = new EventEmitter<void>();
    @Output() error = new EventEmitter<string>();

    languageQueue: LanguageStatus[] = [];
    isProcessing: boolean = false;
    overallError: string | null = null;
    private worker: Worker;
    private currentLangIndex = 0;

    constructor(
        public wizardData: WizardDataService
    ) {}

    ngOnInit(): void {
        this.languageQueue = this.wizardData.targetLanguages.map((lang) => ({
            code: lang,
            status: 'pending',
            message: 'Waiting in queue...',
        }));
        this.initializeWorker();
        this.startProcessingQueue();
    }

    ngOnDestroy(): void {
        if (this.worker) {
            this.worker.terminate();
        }
    }

    private initializeWorker(): void {
        if (typeof Worker !== 'undefined') {
            this.worker = new Worker(
                new URL('@workers/translation.worker', import.meta.url),
                { type: 'module', name: 'translation' }
            );
            this.worker.onmessage = ({ data }) => {
                this.handleWorkerMessage(data);
            };
        } else {
            this.overallError = 'Web workers are not supported in this browser.';
            this.error.emit(this.overallError);
        }
    }

    private handleWorkerMessage(data: any): void {
        const langStatus = this.languageQueue.find(l => l.code === data.lang);
        if (!langStatus) return;

        switch (data.type) {
            case 'status':
                langStatus.message = data.message;
                break;
            case 'skipped':
                langStatus.status = 'skipped';
                langStatus.message = data.message;
                this.processNextInQueue();
                break;
            case 'complete':
                this.wizardData.translationResults.set(langStatus.code, {
                    language: langStatus.code,
                    newTranslations: data.newTranslations,
                    fullMergedJson: {},
                });
                langStatus.status = 'complete';
                langStatus.message = 'Translation successful!';
                this.processNextInQueue();
                break;
            case 'error':
                langStatus.status = 'error';
                langStatus.message = `Failed: ${data.message}`;
                this.overallError = `Error during translation for ${langStatus.code.toUpperCase()}. Please check your API key and funds.`;
                this.isProcessing = false;
                this.error.emit(this.overallError);
                break;
        }
    }

    private startProcessingQueue(): void {
        this.isProcessing = true;
        this.wizardData.translationResults.clear();
        this.currentLangIndex = 0;
        this.processNextInQueue();
    }

    private processNextInQueue(): void {
        if (this.currentLangIndex >= this.languageQueue.length) {
            this.isProcessing = false;
            if (!this.overallError) {
                this.completed.emit();
            }
            return;
        }

        if (this.overallError) {
            this.isProcessing = false;
            return;
        }

        const langStatus = this.languageQueue[this.currentLangIndex];
        langStatus.status = 'processing';
        langStatus.message = 'Sent to worker...';

        const workerData = {
            lang: langStatus.code,
            baseJson: this.wizardData.baseJson,
            targetJson: this.wizardData.targetJsonMap.get(langStatus.code) || {},
            apiKey: this.wizardData.apiKey,
            selectedModel: this.wizardData.selectedModel,
            baseLanguage: this.wizardData.baseLanguage,
        };

        this.worker.postMessage(workerData);
        this.currentLangIndex++;
    }
}
